/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initAttr = {
  id: '',
  code: '',
  type_name: '',
  date_record: '',
  remark: '',
  user_name: '',
  status: 'edit',
};

function PurchaseRequestView({ pr_id }) {
  const mod = 'rp_purchase';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const open = () => {
    setLoading(true);
    setAttr(initAttr);

    FetchData('OPTIONS', `/${mod}/request`, app.token, {
      id: pr_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);

        setAttr(result.data);
        setList(result.list);
        setModalOpen(true);
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.product_name} src={item.image} variant='rounded' />;
  };

  const renderInfo = () => {
    return (
      <MDBox>
        <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
          <MDTypography mb={3} variant='h5'>
            #{attr.code}
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='ประเภทขอซื้อ'
                value={attr.type_name}
                variant='standard'
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='ผู้ขอซื้อ'
                value={attr.user_name}
                variant='standard'
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='วันที่ขอซื้อ'
                value={Func.dateDisplay(attr.date_record)}
                variant='standard'
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='วันที่ใช้งาน'
                value={Func.dateDisplay(attr.date_use)}
                variant='standard'
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='หมายเหตุขอซื้อ'
                value={attr.remark}
                variant='standard'
                multiline
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
        <MDBox>
          <MDButton href={`/purchase_request/${attr.id}`} variant='gradient' color='info' fullWidth>
            <Icon>open_in_new</Icon> เปิด
          </MDButton>
        </MDBox>
      </MDBox>
    );
  };

  const statusDisplay = (item) => {
    return (
      <Alert
        icon={<Icon>{item.status_icon}</Icon>}
        severity={item.status_color}
        sx={{
          fontSize: '14px',
          padding: '0 10px',
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: 'nowrap',
        }}
      >
        {item.status_name}
      </Alert>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <MDBox pl={4} pt={2} pr={2}>
          <Grid container>
            <Grid item xs={6}>
              <MDTypography variant='h5'>#{attr.code}</MDTypography>
            </Grid>
            <Grid item xs={6} className='text-right'>
              <MDButton href={`/purchase_request/${attr.id}`} variant='gradient' color='info'>
                <Icon>open_in_new</Icon> เปิด
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>No.</TableCell>
                <TableCell align='center'>รูป</TableCell>
                <TableCell>Barcode</TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell>ลักษณะ</TableCell>
                <TableCell align='right'>ราคาซื้อ</TableCell>
                <TableCell align='right'>จำนวน</TableCell>
                <TableCell>หน่วย</TableCell>
                <TableCell align='right'>รวม</TableCell>
                <TableCell>ผู้จัดจำหน่าย</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell align='center' width='100'>
                    {i + 1}
                  </TableCell>
                  <TableCell width='40'>{imageDisplay(row)}</TableCell>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>
                    <MDBox>{row.product_sku}</MDBox>
                    <MDBox>{row.product_name}</MDBox>
                  </TableCell>
                  <TableCell>
                    {row.size_name === '' ? null : <MDBox>ขนาด : {row.size_name}</MDBox>}
                    {row.color_name === '' ? null : <MDBox>สี : {row.color_name}</MDBox>}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.cost)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                  <TableCell align='left'>{row.unit_name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.amount)}</TableCell>
                  <TableCell align='left'>{row.supplier_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const btnOpen = () => {
    if (pr_id <= 0) return null;
    return (
      <MDButton variant='outlined' tabIndex={-1} color='info' size='small' onClick={open}>
        ใบขอซื้อ
      </MDButton>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          ใบขอซื้อ
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}

      {renderLoading()}
    </div>
  );
}

PurchaseRequestView.propTypes = {
  pr_id: PropTypes.number.isRequired,
};

export default PurchaseRequestView;
